import {QueryKey, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import api from "@/lib/api";
import {normalizePage} from "@/lib/paging.ts";

// ───────────────────────────────────────────────────────────────────────────────
// Types
// ───────────────────────────────────────────────────────────────────────────────
export type BannerId = string;

export type Banner = {
    id: BannerId;
    title: string;
    type: string;
    placement: string;
    imageUrl: string;
    targetUrl?: string | null;
    startAt: string;
    endAt: string;
    enabled: boolean;
};

export type SaveBannerInput = {
    id?: BannerId;
    title: string;
    type: string;
    placement: string;
    file?: File;
    targetUrl?: string | null;
    startAt: string;
    endAt: string;
    enabled: boolean;
    onUploadProgress?: (pct: number) => void;
};

export type SortField = "title" | "type" | "placement" | "startAt" | "endAt" | "enabled";
export type SortOrder = "asc" | "desc";

export type HookParams = {
    page?: number;
    size?: number;
    sortBy?: SortField;
    sortOrder?: SortOrder;
};

export type BannerPage = ReturnType<typeof normalizePage<Banner>>;

// ───────────────────────────────────────────────────────────────────────────────
// Constants
// ───────────────────────────────────────────────────────────────────────────────
const PUBLIC_BASE = "/api/v1/public/banners";
const ADMIN_BASE = "/api/v1/editor/banners";

const DEFAULTS = {
    page: 0,
    size: 10,
    sortBy: "title" as SortField,
    sortOrder: "desc" as SortOrder,
};

// ───────────────────────────────────────────────────────────────────────────────
// Query Keys
// ───────────────────────────────────────────────────────────────────────────────
export const bannerKeys = {
    all: ["banners"] as const,
    list: (params?: HookParams) => [...bannerKeys.all, "list", params ?? {}] as const,
    byType: (type: string) => [...bannerKeys.all, "byType", type] as const,
    byPlacement: (placement: string) => [...bannerKeys.all, "byPlacement", placement] as const,
};

// ───────────────────────────────────────────────────────────────────────────────
// Fetchers
// ───────────────────────────────────────────────────────────────────────────────
function emptyPage(page = DEFAULTS.page, size = DEFAULTS.size): BannerPage {
    return normalizePage<Banner>({page, size, total: 0, content: []});
}

async function fetchBanners(params: HookParams = {}): Promise<BannerPage> {
    const page = params.page ?? DEFAULTS.page;
    const size = params.size ?? DEFAULTS.size;
    const sortBy = params.sortBy ?? DEFAULTS.sortBy;
    const sortOrder = params.sortOrder ?? DEFAULTS.sortOrder;

    const sort = `${sortBy},${sortOrder}`;
    const {data} = await api.get(PUBLIC_BASE, {params: {page, size, sort}});

    if (Array.isArray(data?.content)) return normalizePage<Banner>(data);
    if (Array.isArray(data?.items) && typeof data?.total === "number") return data;
    return emptyPage(page, size);
}

async function fetchBannerByType(type: string): Promise<Banner> {
    const {data} = await api.get(`${PUBLIC_BASE}/type/${encodeURIComponent(type)}`);
    return data;
}

async function fetchBannerByPlacement(placement: string): Promise<Banner> {
    const {data} = await api.get(`${PUBLIC_BASE}/placement/${encodeURIComponent(placement)}`);
    return data;
}

// ───────────────────────────────────────────────────────────────────────────────
// Queries
// ───────────────────────────────────────────────────────────────────────────────
export function useBanners(params?: HookParams) {
    const q: HookParams = {
        page: params?.page ?? DEFAULTS.page,
        size: params?.size ?? DEFAULTS.size,
        sortBy: params?.sortBy ?? DEFAULTS.sortBy,
        sortOrder: params?.sortOrder ?? DEFAULTS.sortOrder,
    };

    return useQuery({
        queryKey: bannerKeys.list(q),
        queryFn: () => fetchBanners(q),
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        placeholderData: (prev) => prev,
    });
}

export function useBannersByType(type: string) {
    return useQuery({
        queryKey: bannerKeys.byType(type),
        queryFn: () => fetchBannerByType(type),
        enabled: Boolean(type),
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        placeholderData: (prev) => prev,
    });
}

export function useBannersByPlacement(placement: string) {
    return useQuery({
        queryKey: bannerKeys.byPlacement(placement),
        queryFn: () => fetchBannerByPlacement(placement),
        enabled: Boolean(placement),
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        placeholderData: (prev) => prev,
    });
}

// ───────────────────────────────────────────────────────────────────────────────
// Mutations
// ───────────────────────────────────────────────────────────────────────────────
export function useSaveBanner() {
    const qc = useQueryClient();

    return useMutation({
        mutationKey: [...bannerKeys.all, "save"],
        mutationFn: async (payload: SaveBannerInput): Promise<Banner> => {
            const form = new FormData();
            form.append("title", payload.title.trim());
            form.append("type", payload.type);
            form.append("placement", payload.placement);
            form.append("startAt", payload.startAt);
            form.append("endAt", payload.endAt);
            form.append("enabled", payload.enabled ? "true" : "false");

            if (payload.file) form.append("file", payload.file);
            if (payload.targetUrl) form.append("targetUrl", payload.targetUrl);

            const cfg: any = {headers: {"Content-Type": "multipart/form-data"}};

            if (payload.onUploadProgress) {
                cfg.onUploadProgress = (evt: ProgressEvent) => {
                    if (!evt.total) return;
                    payload.onUploadProgress!(Math.round((evt.loaded * 100) / evt.total));
                };
            }

            if (payload.id) {
                const {data} = await api.put(`${ADMIN_BASE}/${payload.id}`, form, cfg);
                return data;
            } else {
                const {data} = await api.post(ADMIN_BASE, form, cfg);
                return data;
            }
        },
        onSuccess: () => {
            // Invalidate all banner queries (lists + lookups) to stay simple & correct
            qc.invalidateQueries({queryKey: bannerKeys.all});
        },
    });
}

export function useDeleteBanner() {
    const qc = useQueryClient();

    return useMutation({
        mutationKey: [...bannerKeys.all, "delete"],
        mutationFn: async (id: BannerId) => {
            await api.delete(`${ADMIN_BASE}/${id}`);
            return id;
        },
        onMutate: async (id) => {
            // Cancel outgoing refetches so we don't overwrite our optimistic update
            await qc.cancelQueries({queryKey: bannerKeys.all});

            // Snapshot all related queries
            const snapshots = qc.getQueriesData<unknown>({queryKey: bannerKeys.all});

            // Optimistically remove the banner from any paged lists we find
            snapshots.forEach(([key, value]) => {
                const page = value as Partial<BannerPage> | undefined;
                if (page && Array.isArray(page.items)) {
                    const next: BannerPage = {
                        ...(page as BannerPage),
                        items: page.items.filter((b: any) => b.id !== id),
                        total: Math.max(0, (page.total as number) - 1),
                    };
                    qc.setQueryData(key, next);
                }
            });

            return {snapshots};
        },
        onError: (_err, _id, ctx) => {
            // Roll back from snapshots if we have them
            ctx?.snapshots?.forEach(([key, value]: [unknown, unknown]) => {
                qc.setQueryData(<QueryKey>key, value);
            });
        },
        onSettled: () => {
            // Ensure server truth wins in the end
            qc.invalidateQueries({queryKey: bannerKeys.all});
        },
    });
}
